/* ============================================================
   ZNAKI · composer.js
   Algorytm kompozycji nieskończonej tablicy.

   Kręgosłup: figury ciężkie rozmieszczone wzdłuż osi pionowej
   w odstępach [gapMin, gapMax] vh. Ponieważ gapMax ≤ 0.5 vh,
   w każdym oknie o wysokości 1 vh znajdują się zawsze
   co najmniej 2 figury ciężkie — wymóg koncepcji.

   Wokół każdej ciężkiej: satelity (figury lekkie w relacjach
   kompozycyjnych). Pomiędzy: wolne figury lekkie na obrzeżach.
   Wszystko deterministyczne względem ziarna (seed).
   ============================================================ */

import { PARAMS } from './config';
import { lerp, rngFor } from './utils';
import { makeHeavy, makeSatellites, makeFree } from './figures';

class Composer {

  constructor(seed) {
    this.regen(seed);
  }

  regen(seed) {
    this.seed = seed;

    /* zamrożone w chwili generacji (spójność kompozycji) */
    const P = PARAMS.comp;
    this._gapMin = P.heavyGapMin;
    this._gapMax = Math.min(P.heavyGapMax, 0.5); /* twarda gwarancja ≥2 */
    this._sideAmp = P.sideAmp;
    this._satMax = P.satMax;
    this._freeMax = P.freeMax;

    /* pozycje kręgosłupa: indeks k → yv [vh]; rosną w obie strony */
    this._spineY = new Map([[0, 0.62]]);
    this._kMin = 0;
    this._kMax = 0;

    this._heavyCache = new Map();  /* k → [figury ciężkie] */
    this._satCache = new Map();    /* k → [satelity] */
    this._freeCache = new Map();   /* c → [wolne figury] */
  }

  _gap(k) {
    const r = rngFor(this.seed, 'gap', k)();
    return lerp(this._gapMin, this._gapMax, r);
  }

  /* rozbuduj kręgosłup, aż pokryje zakres [yTop, yBottom] */
  _expand(yTop, yBottom) {
    while (this._spineY.get(this._kMax) < yBottom) {
      const y = this._spineY.get(this._kMax) + this._gap(this._kMax);
      this._kMax++;
      this._spineY.set(this._kMax, y);
    }
    while (this._spineY.get(this._kMin) > yTop) {
      const y = this._spineY.get(this._kMin) - this._gap(this._kMin - 1);
      this._kMin--;
      this._spineY.set(this._kMin, y);
    }
  }

  _heavies(k) {
    let h = this._heavyCache.get(k);
    if (!h) {
      h = makeHeavy(
        this.seed, k, this._spineY.get(k), this._sideAmp,
        this._gap(k - 1), this._gap(k)
      );
      this._heavyCache.set(k, h);
    }
    return h;
  }

  _sats(k) {
    let s = this._satCache.get(k);
    if (!s) {
      s = makeSatellites(this.seed, k, this._heavies(k), this._satMax);
      /* satelity dziedziczą yv kotwicy */
      this._satCache.set(k, s);
    }
    return s;
  }

  _free(c) {
    let f = this._freeCache.get(c);
    if (!f) {
      f = makeFree(this.seed, c, this._freeMax);
      this._freeCache.set(c, f);
    }
    return f;
  }

  /* wszystkie figury potencjalnie obecne w kadrze przy danym scrollu */
  collect(scroll) {
    const yTop = scroll - 1.0;      /* zapas nad kadrem (wyjazdy, parallax) */
    const yBottom = scroll + 2.2;   /* zapas pod kadrem (wjazdy) */
    this._expand(yTop, yBottom);

    const out = [];

    for (let k = this._kMin; k <= this._kMax; k++) {
      const y = this._spineY.get(k);
      if (y < yTop || y > yBottom) continue;
      const hs = this._heavies(k);
      for (const f of hs) out.push(f);
      const ss = this._sats(k);
      for (const f of ss) out.push(f);
    }

    const cTop = Math.floor(yTop);
    const cBottom = Math.floor(yBottom);
    for (let c = cTop; c <= cBottom; c++) {
      const fs = this._free(c);
      for (const f of fs) out.push(f);
    }

    this._prune(scroll);
    return out;
  }

  /* liczba figur ciężkich w pełni lub częściowo widocznych w kadrze
     (uwzględnia maskę comp.heavyDensity) */
  visibleHeavyCount(scroll) {
    const hd = PARAMS.comp.heavyDensity;
    let n = 0;
    for (let k = this._kMin; k <= this._kMax; k++) {
      const y = this._spineY.get(k);
      if (y >= scroll - 0.05 && y <= scroll + 1.05 && this._heavies(k)[0].rank <= hd) n++;
    }
    return n;
  }

  /* sprzątanie pamięci przy długich podróżach */
  _prune(scroll) {
    if (this._heavyCache.size < 160) return;
    for (const k of this._heavyCache.keys()) {
      const y = this._spineY.get(k);
      if (y < scroll - 12 || y > scroll + 12) {
        this._heavyCache.delete(k);
        this._satCache.delete(k);
      }
    }
    for (const c of this._freeCache.keys()) {
      if (c < scroll - 12 || c > scroll + 12) this._freeCache.delete(c);
    }
  }
}

export default Composer;
